import { useEffect, useMemo } from 'react'

import { Spinner } from '@wartek-id/spinner'

import { CardOrganizationProgress } from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardOrganizationProgress'
import CardFilledBySystem from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardFilledBySystem'
import CardReferensi from 'app/KinerjaPage/components/CardReferensi'
import CardEvaluasiKinerja from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardEvaluasiKinerja'
import CardKinerjaSteps from 'app/KinerjaShared/components/CardKinerjaSteps'
import CardKinerjaHarian from 'app/KinerjaHarian/components/CardKinerjaHarian'
import { KinerjaHarianRoleProvider } from 'app/KinerjaHarian/components/KinerjaHarianRoleProvider'
import KinerjaSidebar from 'app/KinerjaShared/components/KinerjaSidebar'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import CardKinerjaStatus from '../components/CardKinerjaStatus'
import CardPengaliranKinerjaStatus from '../components/CardPengaliranKinerjaStatus'
import CardPelaksanaanCompleted from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardPelaksanaanCompleted'
import { BannerGantiPeriode } from 'app/KinerjaShared/components/BannerGantiPeriode'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { formatPeriod } from 'app/KinerjaShared/utils/period'
import { isAfterStep, isDateStarted } from 'app/KinerjaShared/utils/pelaksanaan'
import CardPelaksanaanNew from 'app/KinerjaPage/components/CardPelaksanaanNew'
import CardEvaluasiKinerjaTahunan from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardEvaluasiKinerjaTahunan'
import { InvalidUserNoAccess } from 'api/kinerja/kepala-sekolah/config'
import BannerPengaliranEkin from 'app/KinerjaShared/components/BannerPengaliranEkin'
import useToggleSdkContext from 'utils/useToogleSdk'
import usePelaksanaan from 'app/KinerjaPage/pages/PelaksanaanV2/hooks/usePelaksanaan'
import { useIsPrior2025 } from 'app/KinerjaShared/hooks/useIsPrior2025'
import {
  PENGELOLAAN_KINERJA_GURU_PATH,
  PENGELOLAAN_KINERJA_PAMONG_PATH,
} from 'app/KinerjaShared/constants/path'
import { usePengelolaanKinerjaBaseUrl } from 'app/KinerjaShared/hooks/usePengelolaanKinerjaBaseUrl'
import { AnnouncementBanner } from 'app/KinerjaShared/components/AnnouncementBanner'

interface BerandaGuruProps {
  isPengaliranKinerjaStatusEnabled?: boolean
}

const BerandaGuru = ({
  isPengaliranKinerjaStatusEnabled = false,
}: BerandaGuruProps) => {
  const {
    period: periodData,
    periods,
    periodNo,
    periodYear,
  } = usePeriodContext()
  const isPriorTo2025 = useIsPrior2025()

  const toggleValue = useToggleSdkContext()

  const nextPeriod = useMemo(() => {
    const indexPeriod = periods?.findIndex((item) => item.id === periodData?.id)
    return indexPeriod !== undefined && indexPeriod > -1
      ? periods[indexPeriod + 1]
      : null
  }, [periods, periodData])

  const formattedNextPeriod = useMemo(() => {
    if (nextPeriod) {
      return formatPeriod(nextPeriod.startDate, nextPeriod.endDate)
    }
    return ''
  }, [nextPeriod])

  const { isLoading, isGuru, isPamong, isSuccess } = useRoleContext()

  const isSPNFEnabled = toggleValue?.['kinerja-spnf-enabled'] === true

  const baseUrl = usePengelolaanKinerjaBaseUrl()
  const isInPamongPage = baseUrl === PENGELOLAAN_KINERJA_PAMONG_PATH

  const {
    isLoadingPelaksanaan,
    dataPelaksanaan,
    dataPerencanaan,
    isPerencanaanFetched,
    refetchSKP,
    isErrorPelaksanaan,
  } = usePelaksanaan(
    isGuru || isPamong,
    isSPNFEnabled
      ? {
          role: isInPamongPage ? 'GURU_SPNF' : 'GURU',
        }
      : {}
  )

  useEffect(() => {
    // Page Guard: if the user is not guru or pamong, throw error to be caught by error boundary
    if (isSuccess && !(isGuru || isPamong)) {
      throw new InvalidUserNoAccess('', null)
    }
  }, [isGuru, isPamong, isSuccess])

  const isResolved = Array.isArray(periods) && periods.length > 0
  const isPelaksanaanStarted =
    isResolved && isDateStarted(periodData.periodeKinerja)
  const isEvaluated = dataPelaksanaan?.data?.evaluationDocumentState === 'READY'
  const isAnnuallyEvaluated =
    dataPelaksanaan?.data?.annualEvaluationDocumentState === 'READY'
  const isPelaksanaanAfterFormD = isAfterStep(
    dataPelaksanaan?.data?.state,
    'DOCUMENT_REFLECTION_FOLLOW_UP'
  )

  const isGuruCompleted =
    isPelaksanaanAfterFormD &&
    dataPelaksanaan?.data?.realisasiState?.additionalTask === 'COMPLETED' &&
    dataPelaksanaan?.data?.realisasiState?.competencyImprovement === 'COMPLETED'

  const isNotReady = isLoading || isLoadingPelaksanaan
  const showPengaliranEkin =
    toggleValue?.['kinerja-pengaliran-ekin-enabled'] &&
    (dataPerencanaan?.data?.status === 'READY' ||
      dataPerencanaan?.data?.status === 'WAITING_EVALUATION')
  const showKinerjaHarianEntryPoint =
    toggleValue?.['kinerja-harian-enabled'] || false

  return (
    <div className="pl-64">
      <div className="flex flex-col w-full gap-6 p-6">
        {isNotReady ? (
          <div className="flex justify-center w-full">
            <Spinner />
          </div>
        ) : (
          <>
            <KinerjaSidebar
              sticky
              activeMenu={isPamong ? 'pamong-pegawai' : 'guru-pegawai'}
            />
            {nextPeriod?.isApproved && (
              <BannerGantiPeriode
                title={`Periode ${formattedNextPeriod} sudah dimulai oleh atasan`}
                subtitle="Ganti periode untuk mulai perencanaan dan pelaksanaan. Tenang, periode sebelumnya tetap dapat diakses."
              />
            )}

            {periodYear === 2026 && (
              <AnnouncementBanner
                title="[Periode 2026] Kami memperbarui indikator Target Perilaku pada Praktik Kinerja Guru"
                description="Silakan cek kembali indikator target perilaku pilihan Anda. Anda masih dapat mengubah jika dirasa kurang sesuai. Diskusikan perubahan ini dengan Kepala Sekolah jika diperlukan."
                linkHref="https://pusatinformasi.ult.kemendikdasmen.go.id/hc/id/articles/55506086533017-Tentang-Penyempurnaan-Formulir-Diskusi-Persiapan-pada-Pengelolaan-Kinerja-Guru"
                linkText="Ke Pusat Bantuan"
              />
            )}

            {showPengaliranEkin && <BannerPengaliranEkin jabatan="guru" />}

            {dataPelaksanaan?.data?.filledBySystem && (
              <CardFilledBySystem
                isGuru={true}
                storageId={dataPelaksanaan?.data?.contentId || 'guru'}
                shouldInitiallyShowPopUp={true}
              />
            )}

            {isPengaliranKinerjaStatusEnabled && (
              <CardPengaliranKinerjaStatus
                statusUnor={dataPerencanaan?.data?.statusUnor}
                statusPengaliran={dataPerencanaan?.data?.statusPengaliran}
              />
            )}

            {isPelaksanaanAfterFormD &&
              !!dataPelaksanaan?.data?.organizationProgress &&
              isPelaksanaanStarted &&
              !isAnnuallyEvaluated && (
                <CardOrganizationProgress
                  activeStep={dataPelaksanaan.data.organizationProgress}
                />
              )}

            {isGuruCompleted && !isEvaluated && isPelaksanaanStarted && (
              <CardPelaksanaanCompleted period={periodNo} year={periodYear} />
            )}

            {isPelaksanaanStarted && isAnnuallyEvaluated && (
              <CardEvaluasiKinerjaTahunan year={periodYear} />
            )}

            {isEvaluated && isPelaksanaanStarted && isPriorTo2025 && (
              <CardEvaluasiKinerja />
            )}

            {/* if perencanaan fetched but perencanaan not ready or the Pelaksanaan is not started yet */}
            {isPerencanaanFetched &&
              (dataPerencanaan?.data?.status !== 'READY' ||
                !isPelaksanaanStarted) && (
                <CardKinerjaStatus
                  skpId={dataPerencanaan?.data?.id}
                  status={dataPerencanaan.data.status}
                  onSuccessUpdateSKP={refetchSKP}
                  tipe={dataPerencanaan.data.tipe}
                />
              )}

            {/* if perencanaan fetched and pelaksanaan already started */}
            {isPerencanaanFetched &&
              isPelaksanaanStarted &&
              dataPerencanaan?.data?.status === 'READY' && (
                <CardPelaksanaanNew
                  dataPelaksanaan={dataPelaksanaan?.data}
                  isError={isErrorPelaksanaan}
                />
              )}
          </>
        )}

        {showKinerjaHarianEntryPoint && (
          <KinerjaHarianRoleProvider role="GURU">
            <CardKinerjaHarian
              ctaHref={`${PENGELOLAAN_KINERJA_GURU_PATH}/harian`}
            />
          </KinerjaHarianRoleProvider>
        )}

        <CardKinerjaSteps />

        <CardReferensi />
      </div>
    </div>
  )
}

export default BerandaGuru
